/**
 * ADMIN-HOME-001/003 (Jun 2026) — TitleFlow Admin Dashboard landing page.
 * Premium iteration: time-based greeting + live clock, count-up KPI cards,
 * gradient icon containers, interactive quick-access cards (lift / glow /
 * arrow slide / ripple), prominent Operations hero card, glass surfaces,
 * staggered entrance animations and skeleton loaders.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserCheck, FileText, Clock, KeyRound, ShieldCheck, Shield,
  ClipboardList, Landmark, Inbox, MonitorCog, HeartPulse, BarChart3,
  ArrowRight, CalendarDays, Zap, AlertTriangle, Settings2, Banknote, HeartHandshake,
  ServerCog, Target,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { MigrationProgressCard } from '../../components/admin/MigrationProgressCard';
import './AdminHomePage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

const METRIC_DEFS = [
  { key: 'total_users', label: 'Total Users', sub: 'All registered users', icon: Users, grad: 'from-blue-500 to-indigo-600', glow: 'shadow-blue-500/25' },
  { key: 'active_users', label: 'Active Users', sub: 'Currently active', icon: UserCheck, grad: 'from-emerald-500 to-green-600', glow: 'shadow-emerald-500/25' },
  { key: 'orders_today', label: 'Orders Today', sub: 'All order types', icon: FileText, grad: 'from-indigo-500 to-violet-600', glow: 'shadow-indigo-500/25' },
  { key: 'pending_orders', label: 'Pending Orders', sub: 'Awaiting assignment', icon: Clock, grad: 'from-amber-400 to-orange-500', glow: 'shadow-amber-500/25' },
  { key: 'recent_logins_24h', label: 'Recent Logins (24h)', sub: 'Signed in last 24 hours', icon: KeyRound, grad: 'from-violet-500 to-purple-600', glow: 'shadow-violet-500/25' },
];

// ADMIN-HOME-005 (Aug 2026): landing page is a high-level dashboard launcher.
// Four premium dashboard cards; module-level functions stay in their menus.
const HERO_DASHBOARDS = [
  { label: 'Operations Dashboard', desc: 'Production orders, workflows, assignments and team activity.', cta: 'Open Operations', icon: MonitorCog, path: '/dashboard', grad: 'from-blue-600 via-indigo-600 to-violet-700', shadow: 'shadow-indigo-600/30 hover:shadow-indigo-600/40', border: 'border-indigo-500/40', sub: 'text-indigo-100' },
  { label: 'Admin Dashboard', desc: 'System administration, configuration and monitoring.', cta: 'Open Admin', icon: Settings2, path: '/admin/users', grad: 'from-slate-700 via-slate-800 to-slate-900', shadow: 'shadow-slate-700/30 hover:shadow-slate-700/40', border: 'border-slate-600/40', sub: 'text-slate-300' },
  { label: 'Finance Dashboard', desc: 'Billing, pricing, invoices, payments and financial reporting.', cta: 'Open Finance', icon: Banknote, path: '/admin/billing', grad: 'from-emerald-600 via-green-600 to-teal-700', shadow: 'shadow-emerald-600/30 hover:shadow-emerald-600/40', border: 'border-emerald-500/40', sub: 'text-emerald-100' },
  { label: 'HR Dashboard', desc: 'Employees, attendance, leave management and HR administration.', cta: 'Open HR', icon: HeartHandshake, path: '/admin/hr/dashboard', grad: 'from-rose-500 via-pink-600 to-fuchsia-700', shadow: 'shadow-rose-600/30 hover:shadow-rose-600/40', border: 'border-rose-500/40', sub: 'text-rose-100' },
  { label: 'IT Dashboard', desc: 'IT requests, assets, credentials and SLA monitoring.', cta: 'Open IT Operations', icon: ServerCog, path: '/admin/it-dashboard', grad: 'from-[#0b1424] via-blue-800 to-violet-800', shadow: 'shadow-blue-800/30 hover:shadow-blue-700/40', border: 'border-blue-500/40', sub: 'text-blue-100' },
  { label: 'Sales CRM', desc: 'Leads, companies, opportunities and sales activity.', cta: 'Open CRM', icon: Target, path: '/crm', grad: 'from-violet-600 via-purple-600 to-fuchsia-700', shadow: 'shadow-violet-600/30 hover:shadow-violet-600/40', border: 'border-violet-500/40', sub: 'text-violet-100' },
];

const QUICK_ACCESS = [
  { label: 'Audit Logs', desc: 'Review system activity and user actions', icon: ClipboardList, path: '/admin/audit-logs', grad: 'from-emerald-500 to-green-600', glow: 'shadow-emerald-500/30', border: 'hover:border-emerald-300' },
  { label: 'Jurisdiction Directory', desc: 'Manage jurisdictions, collectors and offices', icon: Landmark, path: '/admin/jurisdictions', grad: 'from-orange-400 to-amber-600', glow: 'shadow-orange-500/30', border: 'hover:border-orange-300' },
  { label: 'Analytics', desc: 'View reports, metrics and insights', icon: BarChart3, path: '/analytics', grad: 'from-indigo-500 to-blue-600', glow: 'shadow-indigo-500/30', border: 'hover:border-indigo-300' },
];

const greeting = (d) => {
  const h = d.getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

/* Smooth 0 → value count-up (~700ms ease-out cubic) */
const useCountUp = (value, duration = 700) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return typeof value === 'number' ? display : '—';
};

const ripple = (e, light = false) => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const r = document.createElement('span');
  r.className = `ahp-ripple${light ? ' ahp-ripple--light' : ''}`;
  r.style.width = r.style.height = `${size}px`;
  r.style.left = `${e.clientX - rect.left - size / 2}px`;
  r.style.top = `${e.clientY - rect.top - size / 2}px`;
  el.appendChild(r);
  setTimeout(() => r.remove(), 650);
};

const MetricCard = ({ def, value, delay }) => {
  const n = useCountUp(value);
  return (
    <div
      className="ahp-fade-up bg-white/70 backdrop-blur-md border border-slate-200/80 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`metric-${def.key.replace(/_/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className={`p-2 rounded-xl bg-gradient-to-br ${def.grad} text-white shadow-md ${def.glow}`}>
          <def.icon className="w-4 h-4" />
        </div>
        <span className="text-xs font-medium text-slate-500">{def.label}</span>
      </div>
      <div className="text-3xl font-bold text-slate-900 tracking-tight tabular-nums">{n}</div>
      <div className="text-xs text-slate-500 mt-1">{def.sub}</div>
    </div>
  );
};

const MetricSkeleton = ({ delay }) => (
  <div className="ahp-fade-up bg-white/70 border border-slate-200/80 rounded-2xl p-4" style={{ animationDelay: `${delay}ms` }} data-testid="metric-skeleton">
    <div className="flex items-center gap-2 mb-4">
      <div className="ahp-skeleton w-8 h-8 !rounded-xl" />
      <div className="ahp-skeleton h-3 w-20" />
    </div>
    <div className="ahp-skeleton h-8 w-16 mb-2" />
    <div className="ahp-skeleton h-3 w-24" />
  </div>
);

/* ADMIN-RUN-MOVE-001 (Aug 2026): the exact "Last auto-assignment run" strip
   relocated from the Auto Assignment Dashboard — single authoritative copy.
   Same layout, wording, badges and exclusion warnings. */
const RUN_TEAM_LABELS = { tax: 'Tax Team', search: 'Search Team' };

const LastAutoRunCard = ({ run, loading, error, onRetry }) => {
  if (loading) {
    return (
      <div className="ahp-fade-up bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl p-4 shadow-sm" style={{ animationDelay: '920ms' }} data-testid="last-auto-run-skeleton">
        <div className="flex items-center gap-3">
          <div className="ahp-skeleton w-5 h-5 !rounded-md" />
          <div className="ahp-skeleton h-4 w-64" />
        </div>
        <div className="ahp-skeleton h-3 w-80 mt-3" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="ahp-fade-up bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl p-5 shadow-sm text-sm text-slate-500" style={{ animationDelay: '920ms' }} data-testid="last-auto-run-error">
        Could not load the last run summary.
        <button className="ml-2 underline font-medium text-blue-600" onClick={onRetry} data-testid="last-auto-run-retry">Retry</button>
      </div>
    );
  }
  if (!run) {
    return (
      <div className="ahp-fade-up bg-white/80 backdrop-blur-md border border-dashed border-slate-300 rounded-2xl p-8 shadow-sm text-center" style={{ animationDelay: '920ms' }} data-testid="last-auto-run-empty">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-500 text-white flex items-center justify-center mx-auto mb-3 shadow-md">
          <Zap className="w-5 h-5" />
        </div>
        <div className="text-sm font-medium text-slate-700">No Auto Assignment runs have been executed yet.</div>
        <div className="text-xs text-slate-500 mt-1">Once a manual or scheduled run completes, its summary will appear here.</div>
      </div>
    );
  }

  // Exact strip markup from AutoAssignmentDashboard.jsx ("Last Run Info —
  // Jun 2026: exclusion transparency breakdown"), fed by the same run
  // document shape (summary.last_run ≙ /auto-assign/last-run response).
  const teamLabel = RUN_TEAM_LABELS[run.team] || (run.team ? run.team.replace(/[-_]/g, ' ') : 'this team');

  return (
    <div className="ahp-fade-up bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200" style={{ animationDelay: '920ms' }} data-testid="last-run-breakdown">
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
          <Zap className="h-4 w-4 text-blue-500" />
          <span className="font-medium text-gray-900">Last auto-assignment run:</span>
          {run.status === 'running' ? (
            <span className="text-blue-600 font-medium">In progress…</span>
          ) : (
            <>
              <span className={run.success ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {run.success ? 'Success' : 'Failed'}
              </span>
              <span>•</span>
              <span>{run.orders_assigned}/{run.orders_processed} orders assigned</span>
            </>
          )}
          {run.trigger && (
            <>
              <span>•</span>
              <span className="capitalize">{run.trigger}</span>
            </>
          )}
          {run.run_at && (
            <>
              <span>•</span>
              <span>{formatDistanceToNow(new Date(run.run_at), { addSuffix: true })}</span>
            </>
          )}
        </div>
        {run.diagnostics && typeof run.diagnostics.total_unassigned === 'number' && (
          <div className="mt-2 text-sm text-gray-600" data-testid="last-run-diagnostics">
            <p>
              <span className="font-medium">{run.diagnostics.total_unassigned}</span>
              {' '}unassigned order(s) system-wide •{' '}
              <span className="font-medium">{run.diagnostics.eligible_orders}</span>
              {' '}eligible for {teamLabel}
            </p>
            {Array.isArray(run.diagnostics.exclusion_reasons) &&
              run.diagnostics.exclusion_reasons.length > 0 && (
              <ul className="mt-1 space-y-0.5" data-testid="last-run-exclusion-reasons">
                {run.diagnostics.exclusion_reasons.map((r) => (
                  <li key={r.code} className="flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-semibold">{r.count} excluded</span> — {r.label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminHomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [now, setNow] = useState(new Date());
  const clockRef = useRef(null);

  useEffect(() => {
    clockRef.current = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(clockRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API}/admin/dashboard-overview`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setData(await res.json());
    } catch (err) {
      setLoadError(err.message || 'Failed to load overview');
    }
    setLoading(false);
  }, []);

  // ADMIN-HOME-004: Last Auto Assignment Run widget data
  const [lastRun, setLastRun] = useState(null);
  const [runLoading, setRunLoading] = useState(true);
  const [runError, setRunError] = useState(false);
  const loadLastRun = useCallback(async () => {
    setRunLoading(true);
    setRunError(false);
    try {
      const res = await fetch(`${API}/auto-assignment/auto-assign/last-run`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = await res.json();
      setLastRun(body?.run || null);
    } catch {
      setRunError(true);
    }
    setRunLoading(false);
  }, []);

  useEffect(() => { load(); loadLastRun(); }, [load, loadLastRun]);

  const m = data?.metrics;
  const health = m?.system_health;
  const go = (path) => navigate(path);

  return (
    <div className="ahp-root" data-testid="admin-home-page">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome */}
        <div className="mb-10 ahp-fade-up" style={{ animationDelay: '0ms' }}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="admin-greeting">
                {greeting(now)}, Admin <span role="img" aria-label="wave">👋</span>
              </h1>
              <p className="text-sm text-slate-500 mt-1.5">Here&apos;s a snapshot of your platform today.</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-white/70 backdrop-blur-md border border-slate-200/80 rounded-full px-4 py-2 shadow-sm" data-testid="admin-clock">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional portal live clock */}
              <span>{now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
              <span className="text-slate-300">·</span>
              {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional portal live clock */}
              <span className="font-medium text-slate-700 tabular-nums">{now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>

        {/* System Overview */}
        <h2 className="text-base font-semibold text-slate-800 mb-4 ahp-fade-up" style={{ animationDelay: '60ms' }}>System Overview</h2>
        {loadError ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700 mb-10 ahp-fade-up" data-testid="admin-overview-error">
            Could not load system metrics — {loadError}.
            <button className="ml-2 underline font-medium" onClick={load} data-testid="admin-overview-retry">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-12">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <MetricSkeleton key={i} delay={80 + i * 60} />)
              : (
                <>
                  {METRIC_DEFS.map((def, i) => (
                    <MetricCard key={def.key} def={def} value={m?.[def.key]} delay={80 + i * 60} />
                  ))}
                  <div className="ahp-fade-up bg-white/70 backdrop-blur-md border border-slate-200/80 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                    style={{ animationDelay: `${80 + 5 * 60}ms` }} data-testid="metric-system-health">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-cyan-600 text-white shadow-md shadow-green-500/25">
                        <ShieldCheck className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-medium text-slate-500">System Health</span>
                    </div>
                    <div className={`text-2xl font-bold tracking-tight ${health?.status === 'Excellent' ? 'text-emerald-600' : health?.status === 'Good' ? 'text-lime-600' : 'text-amber-600'}`}>
                      {health?.status ?? '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {health?.detail || ''}{typeof health?.db_latency_ms === 'number' ? ` · DB ${health.db_latency_ms} ms` : ''}
                    </div>
                  </div>
                </>
              )}
          </div>
        )}

        {/* Admin Quick Access — ADMIN-HOME-005: dashboard launcher layout */}
        <h2 className="text-base font-semibold text-slate-800 mb-4 ahp-fade-up" style={{ animationDelay: '420ms' }}>Admin Quick Access</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          {HERO_DASHBOARDS.map((card, i) => (
            <button
              key={card.label}
              onClick={(e) => { ripple(e, true); go(card.path); }}
              className={`ahp-fade-up ahp-rippleable group text-left rounded-2xl p-5 bg-gradient-to-br ${card.grad} text-white shadow-lg ${card.shadow} hover:shadow-xl hover:-translate-y-1.5 transition-all duration-200 border ${card.border} flex flex-col`}
              style={{ animationDelay: `${460 + i * 60}ms` }}
              data-testid={`quick-access-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <card.icon className="w-5 h-5" />
              </div>
              <div className="text-base font-semibold tracking-tight leading-snug">{card.label}</div>
              <div className={`text-sm ${card.sub} mt-1.5 leading-relaxed flex-1`}>{card.desc}</div>
              <div className="flex items-center gap-1.5 text-sm font-medium mt-4 opacity-90 whitespace-nowrap">
                {card.cta}
                <ArrowRight className="w-4 h-4 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-200" />
              </div>
            </button>
          ))}
        </div>

        {/* Secondary quick-access cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {QUICK_ACCESS.map((card, i) => (
            <button
              key={card.label}
              onClick={(e) => { ripple(e); go(card.path); }}
              className={`ahp-fade-up ahp-rippleable group text-left bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl p-5 shadow-sm hover:shadow-xl hover:-translate-y-1.5 ${card.border} transition-all duration-200`}
              style={{ animationDelay: `${520 + i * 50}ms` }}
              data-testid={`quick-access-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${card.grad} text-white flex items-center justify-center mb-4 shadow-md ${card.glow} group-hover:scale-110 group-hover:shadow-lg transition-all duration-200`}>
                <card.icon className="w-5 h-5" />
              </div>
              <div className="font-semibold text-slate-900 tracking-tight">{card.label}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">{card.desc}</div>
              <ArrowRight className="w-4 h-4 text-blue-500 mt-3 -translate-x-1.5 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-200" />
            </button>
          ))}
        </div>

        <MigrationProgressCard />

        {/* Last Auto Assignment Run (ADMIN-HOME-004 — replaces Recent Admin Activities;
            full history lives in the Audit Logs / Auto Assignment modules) */}
        <div className="flex items-center justify-between mb-4 ahp-fade-up" style={{ animationDelay: '880ms' }}>
          <h2 className="text-base font-semibold text-slate-800">Last Auto Assignment Run</h2>
          <button
            className="text-xs font-medium text-blue-600 hover:underline"
            onClick={() => navigate('/team/auto-assignment')}
            data-testid="view-run-history"
          >
            View Full Run History →
          </button>
        </div>
        <LastAutoRunCard run={lastRun} loading={runLoading} error={runError} onRetry={loadLastRun} />
      </div>
    </div>
  );
}
